import Phaser from 'phaser';
import { calculateScaleFactor } from '../config/scaling';
import { createTopBar, checkLocalSavedData, playUISound, handleGameProgressUpdate, updateMiniLevel, displayTooltip, destroyTooltip, displayIndicator, destroyIndicator } from '../helpers/levelManager';

export default class HelpDeskScene extends Phaser.Scene {
    constructor() {
        super({ key: 'HelpDesk' });
        this.currentView = 'welcome'; // 'welcome', 'search', 'faq', 'glossary', 'contact'
        this.activeSection = null;
    }

    init() {
        // Game dimensions and scaling
        this.gameW = this.cameras.main.width;
        this.gameH = this.cameras.main.height;

        // Get scaling information
        const scalingData = calculateScaleFactor(this);
        this.scaleFactor = scalingData.scaleFactor;
        this.safeZoneX = scalingData.safeZoneX;
        this.safeZoneY = scalingData.safeZoneY;
        this.safeZoneWidth = scalingData.safeZoneWidth;
        this.safeZoneHeight = scalingData.safeZoneHeight;
        this.templateY = scalingData.templateY;

        // Responsive font sizing
        this.baseFontSize = 20;
        this.fontSize = this.baseFontSize * this.scaleFactor;

        // Load faqs and glossary items data from JSON
        this.emailsAndAlerts = this.cache.json.get('emailsAndAlerts');
        this.faqs = this.emailsAndAlerts.faq;
        this.glossary = this.emailsAndAlerts.glossary;

        // Add search properties
        this.searchResults = [];
        this.currentSearchTerm = '';

        this.searchInputElement = null;
        this.clearButton = null;
        this.filterTimer = null;

        this.loadSavedData();
    }

    loadSavedData() {
        this.savedUserData = checkLocalSavedData();
        this.savedLevel = this.savedUserData?.userLevel || 0;
        this.savedMiniLevel = this.savedUserData?.userMiniLevel || 0;
        this.currentPlanet = this.savedUserData?.currentPlanet || 1;
    }

    create() {
        this.soundManager = this.scene.get("GameUI").soundManager

        createTopBar(this);

        // Create background tablet
        this.createTabletBackground();

        // Create UI elements
        this.createUIComponents();

        // Set up navigation menu
        this.setupNavigationMenu();

        // Show welcome view by default
        this.showWelcomeView();
    }

    createTabletBackground() {
        // Create main tablet background
        this.helpDeskBG = this.add.image(this.gameW / 2, this.gameH / 2, 'tabletBG')
            .setScale(this.scaleFactor, this.scaleFactor * 1.08);

        // Create app bar at the top of the tablet
        this.helpDeskBar = this.add.image(
            this.gameW / 2,
            this.gameH / 2 - 125 * this.scaleFactor,
            'helpDeskAppBar')
            .setScale(this.scaleFactor).setDepth(10);

        this.appBarText = this.add.rexBBCodeText(
            this.helpDeskBar.x,
            this.helpDeskBar.y,
            'Help Desk',
            {
                fontFamily: 'Gluten',
                fontSize: this.fontSize - 4 * this.scaleFactor,
                color: 'white',
                align: 'left',
                fontStyle: 'bold',
                fontWeight: 700,
                resolution: 2
            }
        ).setOrigin(0.5).setDepth(10);

        let closeButton = this.add.image(
            this.helpDeskBar.x + 230 * this.scaleFactor,
            this.helpDeskBar.y,
            'appCloseButton'
        ).setDepth(10).setScale(this.scaleFactor * 0.5);

        closeButton.setInteractive().on('pointerdown', () => {
            playUISound(this, this.soundManager, "Subtle click 1", 0.25)
            this.scene.stop('HelpDesk');
            this.scene.resume('TabletScene');
            this.searchInputElement.remove();
            this.clearButton.remove();
        });
    }

    createUIComponents() {
        // Create the content container
        const contentX = this.gameW / 2;
        const contentY = this.gameH / 2 + 10 * this.scaleFactor;
        const contentWidth = this.helpDeskBG.displayWidth + 1 * this.scaleFactor;
        const contentHeight = this.helpDeskBG.displayHeight * 0.75;

        // Create content area
        this.contentContainer = this.add.container(contentX, contentY);

        // Background for content area
        this.contentBG = this.add.rectangle(
            0, 0,
            contentWidth, contentHeight,
            0xE5EBFF, 1
        ).setOrigin(0.5);
        this.contentContainer.add(this.contentBG);

        // Create search input field (DOM element)
        this.searchInputElement = document.createElement("input");
        this.searchInputElement.type = "text";
        this.searchInputElement.id = "helpDeskSearch";
        this.searchInputElement.placeholder = "Type in a word or phrase to find help...";
        this.searchInputElement.style.position = "fixed";
        this.searchInputElement.style.width = Math.round(contentWidth - 40 * this.scaleFactor) + "px";
        this.searchInputElement.style.height = Math.round(40 * this.scaleFactor) + "px";
        this.searchInputElement.style.fontSize = Math.round(this.fontSize * 0.8) + "px";
        this.searchInputElement.style.fontFamily = '"Space Grotesk", sans-serif';
        this.searchInputElement.style.border = "2px solid #11044E";
        this.searchInputElement.style.background = "white";
        this.searchInputElement.style.color = "#11044E";
        this.searchInputElement.style.borderRadius = "8px";
        this.searchInputElement.style.padding = "0 15px";
        this.searchInputElement.style.boxSizing = "border-box";
        this.searchInputElement.style.outline = "none";
        this.searchInputElement.style.zIndex = "1000";

        //document.body.appendChild(this.searchInputElement);
        const htmlContainer = document.getElementById("html-ui-container")
        htmlContainer.appendChild(this.searchInputElement)

        // Create clear button
        this.clearButton = document.createElement("div");
        this.clearButton.innerHTML = "×";
        this.clearButton.style.position = "fixed";
        this.clearButton.style.width = Math.round(24 * this.scaleFactor) + "px";
        this.clearButton.style.height = Math.round(24 * this.scaleFactor) + "px";
        this.clearButton.style.fontSize = Math.round(24 * this.scaleFactor) + "px";
        this.clearButton.style.color = "#11044E";
        this.clearButton.style.cursor = "pointer";
        this.clearButton.style.display = "flex";
        this.clearButton.style.alignItems = "center";
        this.clearButton.style.justifyContent = "center";
        this.clearButton.style.fontWeight = "bold";
        this.clearButton.style.userSelect = "none";
        this.clearButton.style.zIndex = "1001";

        //document.body.appendChild(this.clearButton);
        htmlContainer.appendChild(this.clearButton)

        // Set up event listeners
        this.searchInputElement.addEventListener("input", () => {
            this.currentSearchTerm = this.searchInputElement.value;

            // Show/hide clear button
            if (this.searchInputElement.value.length > 0) {
                this.clearButton.style.display = "flex";
            } else {
                this.clearButton.style.display = "none";
            }

            // Perform search with slight delay
            if (this.filterTimer) {
                this.filterTimer.remove();
            }
            this.filterTimer = this.time.delayedCall(300, this.performSearch, [], this);
        });

        // Clear button click handler
        this.clearButton.addEventListener("click", () => {
            this.searchInputElement.value = "";
            this.currentSearchTerm = "";
            this.clearButton.style.display = "none";
            this.performSearch();
        });

        // Focus/blur styling
        this.searchInputElement.addEventListener("focus", () => {
            this.searchInputElement.style.borderColor = "#6a5acd";
            this.searchInputElement.style.boxShadow = "0 0 0 2px rgba(106, 90, 205, 0.2)";
        });

        this.searchInputElement.addEventListener("blur", () => {
            this.searchInputElement.style.borderColor = "#cccccc";
            this.searchInputElement.style.boxShadow = "none";
        });

        // Handle touch events to blur input
        document.addEventListener("touchstart", (event) => {
            if (!this.searchInputElement.contains(event.target) && !this.clearButton.contains(event.target)) {
                this.searchInputElement.blur();
            }
        });

        // Initial positioning
        this.updateSearchInputPosition();

        // Hide initially
        this.searchInputElement.style.display = "none";
        this.clearButton.style.display = "none";
    }

    updateSearchInputPosition() {
        const canvas = this.game.canvas;
        const rect = canvas.getBoundingClientRect();

        // Position relative to the content container
        const contentX = this.contentContainer.x;
        const contentY = this.contentContainer.y;
        const contentHeight = this.contentBG.height;

        // Calculate input position (centered at top of content area)
        const inputX = contentX;
        const inputY = contentY - contentHeight / 2 + 40 * this.scaleFactor;

        // Convert to screen coordinates
        const inputLeft = rect.left + inputX - this.searchInputElement.offsetWidth / 2;
        const inputTop = rect.top + inputY - this.searchInputElement.offsetHeight / 2;

        this.searchInputElement.style.left = `${inputLeft}px`;
        this.searchInputElement.style.top = `${inputTop}px`;

        // Position clear button inside the input field (right side)
        const clearButtonRight = inputLeft + this.searchInputElement.offsetWidth - 30 * this.scaleFactor;
        const clearButtonTop = inputTop + this.searchInputElement.offsetHeight / 2 - this.clearButton.offsetHeight / 2 - 10 * this.scaleFactor;

        this.clearButton.style.left = `${clearButtonRight}px`;
        this.clearButton.style.top = `${clearButtonTop}px`;
    }

    setupNavigationMenu() {
        // Get the width of the tablet for proper button positioning
        const tabletWidth = this.helpDeskBG.displayWidth;
        const menuHeight = 50 * this.scaleFactor;
        const menuY = this.gameH / 2 + this.helpDeskBG.displayHeight / 2 - menuHeight / 2;

        const menuItems = [
            { key: 'welcome', text: 'Homepage' },
            { key: 'search', text: 'Search' },
            { key: 'faq', text: 'FAQs' },
            { key: 'glossary', text: 'Glossary' }
        ];

        // Calculate button width based on tablet width and number of buttons
        const btnWidth = tabletWidth / menuItems.length;
        const startX = this.gameW / 2 - tabletWidth / 2 + btnWidth / 2;

        this.navButtons = [];

        menuItems.forEach((item, index) => {
            const btnX = startX + index * btnWidth;

            // Create button background - perfectly aligned with no gaps
            const btn = this.add.rectangle(
                btnX, menuY,
                btnWidth, menuHeight,
                0x6a5acd, 1  // Default purple color
            ).setOrigin(0.5, 0.5).setInteractive();

            // Add black border
            this.add.rectangle(
                btnX, menuY,
                btnWidth, menuHeight,
                0x000000, 0  // Black color
            ).setOrigin(0.5, 0.5).setStrokeStyle(0.9, 0x11044E);

            // Add button text
            const text = this.add.text(
                btnX, menuY,
                item.text,
                {
                    fontFamily: 'Space Grotesk',
                    fontSize: this.fontSize * 0.85,
                    color: 'white',
                    align: 'center',
                    fontStyle: 'bold',
                    fontWeight: 500,
                    resolution: 2
                }
            ).setOrigin(0.5);

            // Set up button interactivity
            btn.on('pointerdown', () => {
                this.handleNavigation(item.key);
            });

            this.navButtons.push({ key: item.key, btn, text });
        });

        displayTooltip(this, {
            xPos: startX + 190 * this.scaleFactor,
            yPos: menuY - 60 * this.scaleFactor,
            type: "helpDeskTip",
            scale: 1,
            text: "Move through the app by pressing on\nthe different sections of the navigation bar",
            fontScale: -8,
            xTextOffset: 0,
            yTextOffset: -8,
            wrapWidth: 1.1,
        })
        // Set the initial active button
        this.handleNavigation('welcome');
    }

    handleNavigation(view) {
        playUISound(this, this.soundManager, "Subtle click 1", 0.25)
        this.currentView = view;
        this.clearContent();

        // Update active button styling
        this.navButtons.forEach(item => {
            if (item.key === view) {
                item.btn.fillColor = 0x4a3abd; // Darker purple for active
                item.text.setColor('#ffffff'); // White text for active
            } else {
                item.btn.fillColor = 0xf2f2ff; // Light purple/white for inactive
                item.text.setColor('#6a5acd'); // Purple text for inactive
            }
        });

        // Show the appropriate view
        switch (view) {
            case 'welcome':
                this.appBarText.setText('Help Desk');
                this.isSearchVisible = false;
                this.showWelcomeView();
                break;
            case 'search':
                this.appBarText.setText(`Help Desk > [size=${this.fontSize - 10}]Search[/size]`);
                destroyTooltip(this);
                this.isSearchVisible = true;
                this.showSearchView();
                if (this.currentSearchTerm) {
                    this.searchInputElement.value = this.currentSearchTerm;
                    this.clearButton.style.display = "flex";
                }
                break;
            case 'faq':
                this.appBarText.setText(`Help Desk > [size=${this.fontSize - 10}]FAQS[/size]`);
                destroyTooltip(this);
                this.isFAQVisible = true;
                this.isSearchVisible = false;
                this.showFAQView();
                break;
            case 'glossary':
                this.appBarText.setText(`Help Desk > [size=${this.fontSize - 10}]Glossary[/size]`);
                destroyTooltip(this);
                this.isGlossaryVisible = true;
                this.isSearchVisible = false;
                this.showGlossaryView();
                break;
        }
    }

    clearContent() {
        // Remove all dynamic content
        // this.contentContainer.each(child => {
        //     if (child !== this.contentBG &&
        //         child !== this.searchBar &&
        //         child !== this.searchPlaceholder &&
        //         child !== this.searchIcon) {
        //         this.contentContainer.remove(child, true);
        //     }
        // });

        // // Hide search components by default
        // this.searchBar.setVisible(false);
        // this.searchPlaceholder.setVisible(false);
        // this.searchIcon.setVisible(false);

        // // Hide search input element when leaving search view
        // if (this.currentView !== 'search') {
        //     this.searchInputElement.style.display = "none";
        //     this.clearButton.style.display = "none";
        // }
    }

    showWelcomeView() {
        const contentWidth = this.contentBG.width;
        const contentHeight = this.contentBG.height;

        // Welcome text
        const welcomeTitle = this.add.text(
            0, -contentHeight / 2 + 40 * this.scaleFactor,
            'Welcome to Help desk!',
            {
                fontFamily: 'Space Grotesk',
                fontSize: this.fontSize * 1.2,
                color: '#11044E',
                align: 'center',
                fontStyle: 'bold'
            }
        ).setOrigin(0.5, 0.5);

        const welcomeMessage = this.add.text(
            0, -contentHeight / 2 + 80 * this.scaleFactor,
            'Search for help, browse through our frequently asked questions or check out the glossary to refine your tech terminology.',
            {
                fontFamily: 'Space Grotesk',
                fontSize: this.fontSize * 0.9,
                color: '#11044E',
                align: 'center',
                wordWrap: { width: contentWidth - 40 * this.scaleFactor }
            }
        ).setOrigin(0.5, 0);

        this.contentContainer.add([welcomeTitle, welcomeMessage]);
    }

    showSearchView() {
        // Show search input
        this.searchInputElement.style.display = "block";
        this.updateSearchInputPosition();

        // Focus the input
        setTimeout(() => {
            this.searchInputElement.focus();
        }, 100);

        // Initial search results display
        this.performSearch();

        // Set search view visible flag
        this.isSearchVisible = true;
    }

    setupSearchInput() {
        // Make search bar interactive
        this.searchBar.setInteractive();

        // Create invisible input field for text capture
        this.searchInput = this.add.dom(this.searchBar.x, this.searchBar.y).createElement('input', {
            type: 'text',
            placeholder: 'Type a word or phrase to find help...',
            style: `
            width: ${this.searchBar.width - 20}px;
            height: ${this.searchBar.height - 10}px;
            font-size: ${this.fontSize * 0.8}px;
            font-family: 'Space Grotesk';
            border: none;
            outline: none;
            background: transparent;
            padding: 0 10px;
        `
        });

        // Handle input events
        this.searchInput.node.addEventListener('input', (event) => {
            this.currentSearchTerm = event.target.value;
            this.updateSearchPlaceholder();
            this.performSearch();
        });

        // Focus handling
        this.searchBar.on('pointerdown', () => {
            this.searchInput.node.focus();
        });
    }

    // Add method to update placeholder visibility
    updateSearchPlaceholder() {
        if (this.currentSearchTerm.length > 0) {
            this.searchPlaceholder.setVisible(false);
        } else {
            this.searchPlaceholder.setVisible(true);
        }
    }

    // Add the main search method
    performSearch() {
        this.searchResults = [];

        if (this.currentSearchTerm.length === 0) {
            this.displaySearchResults();
            return;
        }

        const searchTerm = this.currentSearchTerm.toLowerCase();

        // Search through FAQs
        this.faqs.forEach((faq, index) => {
            const subjectMatch = faq.subject.toLowerCase().includes(searchTerm);
            const bodyMatch = faq.body.toLowerCase().includes(searchTerm);

            if (subjectMatch || bodyMatch) {
                this.searchResults.push({
                    type: 'faq',
                    title: faq.subject,
                    content: faq.body,
                    index: index,
                    matchType: subjectMatch ? 'title' : 'content'
                });
            }
        });

        // Search through Glossary
        this.glossary.forEach((item, index) => {
            const subjectMatch = item.subject.toLowerCase().includes(searchTerm);
            const bodyMatch = item.body.toLowerCase().includes(searchTerm);

            if (subjectMatch || bodyMatch) {
                this.searchResults.push({
                    type: 'glossary',
                    title: item.subject,
                    content: item.body,
                    index: index,
                    matchType: subjectMatch ? 'title' : 'content'
                });
            }
        });

        this.displaySearchResults();
    }

    // Add method to display search results
    displaySearchResults() {
        // Clear existing results
        if (this.searchResultsContainer) {
            this.contentContainer.remove(this.searchResultsContainer, true);
        }

        const contentWidth = this.contentBG.width;
        const contentHeight = this.contentBG.height;
        const startY = -contentHeight / 2 + 90 * this.scaleFactor; // Leave space for search input

        // Create results container
        this.searchResultsContainer = this.add.container(0, 0);

        if (this.searchResults.length === 0 && this.currentSearchTerm.length === 0) {
            // No search performed yet - show placeholder or instructions
            const placeholderText = this.add.text(
                0, startY + 50 * this.scaleFactor,
                'Type in the search box above to find help topics.',
                {
                    fontFamily: 'Space Grotesk',
                    fontSize: this.fontSize * 0.9,
                    color: '#11044E',
                    align: 'center',
                    wordWrap: { width: contentWidth - 40 * this.scaleFactor }
                }
            ).setOrigin(0.5, 0);

            this.searchResultsContainer.add(placeholderText);
            this.contentContainer.add(this.searchResultsContainer);
            return;
        }

        if (this.searchResults.length === 0) {
            // No results found
            const noResultsText = this.add.text(
                0, startY + 50 * this.scaleFactor,
                `No results found for "${this.currentSearchTerm}".`,
                {
                    fontFamily: 'Space Grotesk',
                    fontSize: this.fontSize * 0.9,
                    color: '#11044E',
                    align: 'center',
                    wordWrap: { width: contentWidth - 40 * this.scaleFactor }
                }
            ).setOrigin(0.5, 0);

            this.searchResultsContainer.add(noResultsText);
            this.contentContainer.add(this.searchResultsContainer);
            return;
        }

        // Results header
        const resultsHeader = this.add.text(
            -contentWidth / 2 + 20 * this.scaleFactor, startY,
            'Results',
            {
                fontFamily: 'Space Grotesk',
                fontSize: this.fontSize * 1.1,
                color: '#11044E',
                align: 'left',
                fontWeight: '700',
                fontStyle: 'bold'
            }
        ).setOrigin(0, 0);

        // Add underline for Results header
        const resultsUnderline = this.add.rectangle(
            resultsHeader.x, resultsHeader.y + 25 * this.scaleFactor,
            contentWidth - 40 * this.scaleFactor, 2 * this.scaleFactor,
            0x11044E
        ).setOrigin(0, 0.5);

        this.searchResultsContainer.add([resultsHeader, resultsUnderline]);

        // Track total content height for scrolling
        let totalContentHeight = 0;

        // Display each result
        this.searchResults.forEach((result, index) => {
            const yPos = startY + 40 * this.scaleFactor + (index * 70 * this.scaleFactor);
            totalContentHeight = Math.max(totalContentHeight, yPos + 70 * this.scaleFactor);

            // Result title
            const resultTitle = this.add.text(
                -contentWidth / 2 + 20 * this.scaleFactor, yPos,
                result.title,
                {
                    fontFamily: 'Space Grotesk',
                    fontSize: this.fontSize * 0.85,
                    color: '#11044E',
                    align: 'left',
                    fontWeight: '600',
                    wordWrap: { width: contentWidth - 120 * this.scaleFactor }
                }
            ).setOrigin(0, 0);

            // Add underline for each result
            const resultUnderline = this.add.rectangle(
                resultTitle.x, resultTitle.y + 20 * this.scaleFactor,
                contentWidth - 60 * this.scaleFactor, 1 * this.scaleFactor,
                0xBECCFF
            ).setOrigin(0, 0.5);

            // Type indicator (clickable link)
            const typeIndicator = result.type === 'faq' ? 'go to FAQs' : 'go to glossary';
            const typeLink = this.add.text(
                contentWidth / 2 - 20 * this.scaleFactor, yPos,
                typeIndicator,
                {
                    fontFamily: 'Space Grotesk',
                    fontSize: this.fontSize * 0.7,
                    color: '#11044E',
                    align: 'right',
                    fontWeight: '500'
                }
            ).setOrigin(1, 0).setInteractive();

            // Make type link clickable
            typeLink.on('pointerdown', () => {
                //playUISound(this, this.soundManager, "Subtle click 1", 0.25);
                if (result.type === 'faq') {
                    this.handleNavigation('faq');
                } else {
                    this.handleNavigation('glossary');
                }
            });

            // Add hover effect to links
            typeLink.on('pointerover', () => {
                typeLink.setColor('#11044E');
            });
            typeLink.on('pointerout', () => {
                typeLink.setColor('#11044E');
            });

            this.searchResultsContainer.add([resultTitle, resultUnderline, typeLink]);
        });

        // Set up scrolling for search results
        this.setupSearchScrolling(totalContentHeight);

        this.contentContainer.add(this.searchResultsContainer);
    }
    // Add scrolling functionality for search results
    setupSearchScrolling(totalContentHeight) {
        const contentHeight = this.contentBG.height;
        const maskHeight = contentHeight - 50 * this.scaleFactor; // Account for search input and header
        const maskWidth = this.contentBG.width + 200 * this.scaleFactor; // Slightly wider than content for aesthetics

        // Only add scrolling if content exceeds viewable area
        if (totalContentHeight > maskHeight) {
            // Create mask for search results
            const maskShape = this.add.rectangle(
                0,
                150 * this.scaleFactor, // Position below search input
                maskWidth,
                maskHeight,
                0xff53ff
            ).setVisible(false);

            const maskGraphics = this.make.graphics();
            maskGraphics.fillRectShape(maskShape);
            const geometryMask = new Phaser.Display.Masks.GeometryMask(this, maskGraphics);
            this.searchResultsContainer.setMask(geometryMask);

            // Add scroll indicator
            const scrollIndicator = this.add.rectangle(
                this.contentBG.width / 2 - 15 * this.scaleFactor,
                100 * this.scaleFactor,
                5 * this.scaleFactor,
                40 * this.scaleFactor,
                0xBECCFF
            ).setAlpha(0.7);

            // Function to update scroll indicator position
            const updateScrollIndicator = () => {
                if (!this.isSearchVisible) return;

                const scrollPercentage = -this.searchResultsContainer.y / (totalContentHeight - maskHeight);
                const scrollbarTrackHeight = maskHeight - scrollIndicator.height;
                const newY = -maskHeight / 2 + scrollIndicator.height / 2 + (scrollPercentage * scrollbarTrackHeight) + 100 * this.scaleFactor;

                scrollIndicator.y = newY;
                scrollIndicator.setAlpha(0.7);

                // Hide indicator after a delay
                this.time.delayedCall(1500, () => {
                    if (this.isSearchVisible) {
                        scrollIndicator.setAlpha(0.3);
                    }
                });
            };

            // Function to update search results position with bounds checking
            const updateSearchPosition = (newY) => {
                const bottomPadding = 50 * this.scaleFactor;
                const boundedY = Phaser.Math.Clamp(
                    newY,
                    -totalContentHeight + maskHeight - bottomPadding,
                    0
                );

                this.searchResultsContainer.y = boundedY;
                updateScrollIndicator();
            };

            // Mouse wheel scrolling
            this.input.on('wheel', (pointer, gameObjects, deltaX, deltaY, deltaZ) => {
                if (!this.isSearchVisible) return;

                const scrollSpeed = 1 * this.scaleFactor;
                updateSearchPosition(this.searchResultsContainer.y - deltaY * scrollSpeed);
            });

            // Touch/drag scrolling
            let isDragging = false;
            let startY = 0;

            this.input.on('pointerdown', (pointer) => {
                if (!this.isSearchVisible) return;

                const contentBounds = this.contentBG.getBounds();
                if (contentBounds.contains(pointer.x, pointer.y)) {
                    isDragging = true;
                    startY = pointer.y - this.searchResultsContainer.y;
                }
            });

            this.input.on('pointermove', (pointer) => {
                if (!isDragging) return;
                updateSearchPosition(pointer.y - startY);
            });

            this.input.on('pointerup', () => {
                isDragging = false;
            });

            // Add scroll indicator to content container
            this.contentContainer.add(scrollIndicator);

            // Initial update of scroll indicator
            updateScrollIndicator();
        }
    }

    clearContent() {
        // Remove all dynamic content
        this.contentContainer.each(child => {
            if (child !== this.contentBG) {
                this.contentContainer.remove(child, true);
            }
        });

        // Hide search input
        this.searchInputElement.style.display = "none";
        this.clearButton.style.display = "none";
        this.searchInputElement.value = "";
        //this.currentSearchTerm = "";

        // Reset visibility flags
        this.isSearchVisible = false;
        this.isFAQVisible = false;
        this.isGlossaryVisible = false;
    }

    shutdown() {
        // Clean up DOM elements
        if (this.searchInputElement && this.searchInputElement.parentNode) {
            document.body.removeChild(this.searchInputElement);
        }
        if (this.clearButton && this.clearButton.parentNode) {
            document.body.removeChild(this.clearButton);
        }

        // Remove event listeners
        document.removeEventListener("touchstart", this.handleTouchOutside);
    }

    showFAQView() {
        const contentWidth = this.contentBG.width;
        const contentHeight = this.contentBG.height;

        // FAQ Title
        const faqTitle = this.add.text(
            -60 * this.scaleFactor, -contentHeight / 2 + 30 * this.scaleFactor,
            'Frequently Asked Questions (FAQs)',
            {
                fontFamily: 'Space Grotesk',
                fontSize: this.fontSize,
                color: '#11044E',
                align: 'left',
                fontWeight: '700',
                fontStyle: 'bold',
                resolution: 2
            }
        ).setOrigin(0.5, 0.5);

        // Underline for FAQ title
        const faqTitleUnderline = this.add.rectangle(
            faqTitle.x / 2 - 200 * this.scaleFactor, faqTitle.y + 20 * this.scaleFactor,
            contentWidth - 60 * this.scaleFactor, 2 * this.scaleFactor,
            0x11044E
        ).setOrigin(0, 0.5);

        // // Close button for FAQ view
        // const closeBtn = this.add.image(
        //     contentWidth / 2 - 20 * this.scaleFactor,
        //     -contentHeight / 2 + 30 * this.scaleFactor,
        //     'closeIcon'
        // ).setScale(this.scaleFactor * 0.6).setInteractive();

        // closeBtn.on('pointerdown', () => {
        //     // Handle close button action
        //     if (this.onClose) {
        //         playUISound(this, this.soundManager, "Subtle click 1", 0.25)
        //         this.onClose();
        //     }
        // });

        // Create scrollable container for FAQ items
        const faqContainer = this.add.container(0, 0);

        // Add FAQ items
        const faqItems = this.faqs;
        const accordionItems = [];

        // Track which accordion item is open
        let openItemIndex = -1;
        let totalContentHeight = 0;

        faqItems.forEach((item, index) => {
            const y = faqTitleUnderline.y + 20 * this.scaleFactor + (index * 40 * this.scaleFactor);

            // Create accordion item container
            const accordionItem = this.add.container(0, y);

            // Add drop-down indicator
            const dropdownIcon = this.add.image(
                contentWidth / 2 - 40 * this.scaleFactor, 0,
                'dropDownClosed',
            ).setOrigin(0.5, 0.5).setScale(this.scaleFactor * 0.6).setInteractive();

            // FAQ question text (header)
            const questionText = this.add.text(
                -contentWidth / 2 + 30 * this.scaleFactor, 0,
                item.subject,
                {
                    fontFamily: 'Space Grotesk',
                    fontSize: this.fontSize * 0.8,
                    color: '#11044E',
                    align: 'left',
                    fontWeight: '700',
                    fontStyle: 'bold',
                    resolution: 2
                }
            ).setOrigin(0, 0.5).setInteractive();

            const faqItemUnderline = this.add.rectangle(
                questionText.x - 1 * this.scaleFactor, questionText.y + 20 * this.scaleFactor,
                contentWidth - 60 * this.scaleFactor, 1 * this.scaleFactor,
                0xBECCFF
            ).setOrigin(0, 0.5);

            // Create the answer container (initially hidden)
            const answerContainer = this.add.container(0, 35 * this.scaleFactor);
            answerContainer.visible = false;

            // Answer text
            const answerText = this.add.text(
                -20 * this.scaleFactor, 0,
                item.body,
                {
                    fontFamily: 'Space Grotesk',
                    fontSize: this.fontSize * 0.8,
                    color: '#11044E',
                    align: 'left',
                    fontWeight: '400',
                    resolution: 2,
                    wordWrap: { width: contentWidth - 80 * this.scaleFactor }
                }
            ).setOrigin(0.5, 0);

            // Add elements to containers
            answerContainer.add([answerText]);
            accordionItem.add([questionText, faqItemUnderline, dropdownIcon, answerContainer]);

            // Make header interactive with improved drag vs. click detection
            dropdownIcon.on('pointerup', (pointer) => {
                // Only toggle if it was a genuine click (quick tap with minimal movement)
                const clickDuration = this.time.now - pointerDownTime;

                if (!isDragging && dragDistance < dragThreshold && clickDuration < clickThreshold) {
                    this.toggleAccordion(index, accordionItems, dropdownIcon);
                }
            });

            questionText.on('pointerup', (pointer) => {
                // Only toggle if it was a genuine click (quick tap with minimal movement)
                const clickDuration = this.time.now - pointerDownTime;

                if (!isDragging && dragDistance < dragThreshold && clickDuration < clickThreshold) {
                    this.toggleAccordion(index, accordionItems, dropdownIcon);
                }
            });

            // Store reference to the accordion item
            accordionItems.push({
                container: accordionItem,
                answerContainer: answerContainer,
                dropdownIcon: dropdownIcon,
                height: answerText.height + 20 * this.scaleFactor
            });

            faqContainer.add(accordionItem);

            // Update the total content height tracking
            totalContentHeight = Math.max(totalContentHeight, y + 80 * this.scaleFactor);
        });

        // Define the viewable area (mask)
        // const maskHeight = this.helpDeskBG.displayHeight - 140 * this.scaleFactor; // Leave some space for title
        // const maskWidth = this.helpDeskBG.displayWidth + 150 * this.scaleFactor;

        const maskHeight = this.contentBG.height - 50 * this.scaleFactor; // Leave some space for title
        const maskWidth = this.contentBG.width + 150 * this.scaleFactor;

        // Position the mask to align with content
        const maskShape = this.add.rectangle(
            0, // Center x position
            this.helpDeskBar.y + 70 * this.scaleFactor,
            maskWidth,
            maskHeight,
            0xff53ff
        ).setVisible(false);

        const maskGraphics = this.make.graphics();
        maskGraphics.fillRectShape(maskShape);

        const geometryMask = new Phaser.Display.Masks.GeometryMask(this, maskGraphics);
        faqContainer.setMask(geometryMask);

        // Add a scroll indicator
        const scrollIndicator = this.add.rectangle(
            contentWidth / 2 - 15 * this.scaleFactor,
            0,
            5 * this.scaleFactor,
            40 * this.scaleFactor,
            0xBECCFF
        ).setAlpha(0.7);

        // Function to update scroll indicator position
        const updateScrollIndicator = () => {
            const calculatedContentHeight = this.getCalculatedContentHeight(accordionItems, openItemIndex, contentHeight);
            const scrollPercentage = -faqContainer.y / (calculatedContentHeight - maskHeight);
            const scrollbarTrackHeight = maskHeight - scrollIndicator.height;
            const newY = -maskHeight / 2 + scrollIndicator.height / 2 + (scrollPercentage * scrollbarTrackHeight);

            scrollIndicator.y = newY;
            scrollIndicator.setAlpha(0.7);

            // Hide indicator after a delay
            this.time.delayedCall(1500, () => {
                scrollIndicator.setAlpha(0.3);
            });
        };


        // Function to update FAQ container position with bounds checking
        const updateFAQPosition = (newY) => {
            const calculatedContentHeight = this.getCalculatedContentHeight(accordionItems, openItemIndex, contentHeight);

            // Add padding to the bottom so last items can be seen clearly
            const bottomPadding = 90 * this.scaleFactor; // Adjust this value as needed

            // Ensure we don't scroll past content boundaries
            const boundedY = Phaser.Math.Clamp(
                newY,
                -calculatedContentHeight + maskHeight - bottomPadding, // Bottom boundary with padding
                0 // Top boundary
            );

            faqContainer.y = boundedY;
            updateScrollIndicator();
        };

        // Helper function to calculate total content height considering open items
        this.getCalculatedContentHeight = (items, openIndex, contentHeight) => {
            let calculatedHeight = totalContentHeight;

            // Add height for opened item if any
            if (openIndex !== -1) {
                calculatedHeight += items[openIndex].height;
            }

            return calculatedHeight;
        };

        // Set up scrolling with mouse wheel
        this.input.on('wheel', (pointer, gameObjects, deltaX, deltaY, deltaZ) => {
            // Only respond to scroll when FAQ is visible
            if (!this.isFAQVisible) return;

            const scrollSpeed = 1 * this.scaleFactor;
            updateFAQPosition(faqContainer.y - deltaY * scrollSpeed);
        });

        // Add dragging functionality with better distinction between clicks and drags
        let isDragging = false;
        let startY = 0;
        let startX = 0;
        let dragDistance = 0;
        let pointerDownTime = 0;
        const dragThreshold = 10; // Distance in pixels before considering it a drag
        const clickThreshold = 200; // Time in ms to distinguish click from hold

        this.input.on('pointerdown', (pointer) => {
            if (!this.isFAQVisible) return;

            // playUISound(this, this.soundManager, "Subtle click 1", 0.25)
            // Check if pointer is within the content area
            const contentBounds = this.contentBG.getBounds();
            if (contentBounds.contains(pointer.x, pointer.y)) {
                isDragging = false;
                startY = pointer.y;
                startX = pointer.x;
                dragDistance = 0;
                pointerDownTime = this.time.now;
            }
        });

        this.input.on('pointermove', (pointer) => {
            if (!this.isFAQVisible) return;

            // playUISound(this, this.soundManager, "Subtle click 1", 0.25)
            const contentBounds = this.contentBG.getBounds();
            if (contentBounds.contains(pointer.x, pointer.y) && pointerDownTime > 0) {
                // Calculate distance moved - use both X and Y for more accurate detection
                const dx = pointer.x - startX;
                const dy = pointer.y - startY;
                const moveDistance = Math.sqrt(dx * dx + dy * dy);

                // Update total drag distance
                dragDistance += moveDistance;

                // If we've moved enough to consider it a drag
                if (dragDistance > dragThreshold) {
                    isDragging = true;
                    // Update scroll position
                    updateFAQPosition(faqContainer.y + (pointer.y - startY) * 0.8);
                }

                // Update start position for next move
                startX = pointer.x;
                startY = pointer.y;
            }
        });

        this.input.on('pointerup', (pointer) => {
            // Keep dragging flag true if significant movement occurred
            const wasDragging = isDragging || dragDistance > dragThreshold;

            // Reset tracking variables
            isDragging = false;
            dragDistance = 0;
            pointerDownTime = 0;
        });

        // Function to toggle accordion items
        this.toggleAccordion = (index, items, clickedIcon) => {
            // If clicking the same item that's already open, close it
            playUISound(this, this.soundManager, "Subtle click 1", 0.25)
            if (openItemIndex === index) {
                items[index].answerContainer.visible = false;
                items[index].dropdownIcon.setTexture('dropDownClosed');
                openItemIndex = -1;

                // Reposition all items to their original positions
                this.repositionAccordionItems(items, -1);
                updateScrollIndicator();
                return;
            }

            // Close previously open item if any
            if (openItemIndex !== -1) {
                items[openItemIndex].answerContainer.visible = false;
                items[openItemIndex].dropdownIcon.setTexture('dropDownClosed');
            }

            // Open clicked item
            items[index].answerContainer.visible = true;
            items[index].dropdownIcon.setTexture('dropDownOpened');
            openItemIndex = index;

            // Reposition all items below the opened one
            this.repositionAccordionItems(items, index);
            updateScrollIndicator();
        };

        // Function to reposition accordion items when one is opened/closed
        this.repositionAccordionItems = (items, openIndex) => {
            let offset = 0;

            items.forEach((item, idx) => {
                const baseY = faqTitleUnderline.y + 20 * this.scaleFactor + (idx * 40 * this.scaleFactor);;

                // Add extra space for the open item
                if (openIndex !== -1 && idx > openIndex) {
                    offset = items[openIndex].height;
                }

                // Animate the repositioning
                this.tweens.add({
                    targets: item.container,
                    y: baseY + offset,
                    duration: 300,
                    ease: 'Power2'
                });
            });
        };

        // Add elements to content container
        this.contentContainer.add([faqTitle, faqTitleUnderline, faqContainer, scrollIndicator]);

        // Store references for later use
        this.faqContainer = faqContainer;
        this.faqMaskHeight = maskHeight;
        this.accordionItems = accordionItems;

        // Set initial state indicator
        this.isFAQVisible = true;

        // Initial update of scroll indicator
        updateScrollIndicator();
    }

    showGlossaryView() {
        const contentWidth = this.contentBG.width;
        const contentBGHeight = this.contentBG.height;

        // Create scrollable container for glossary items
        const glossaryContainer = this.add.container(
            0,
            0
        );

        const glossaryItems = this.glossary;
        let totalContentHeight = 0;

        // Display glossary items
        glossaryItems.forEach((item, index) => {
            const y = -65 * this.scaleFactor + (index * 100 * this.scaleFactor);
            totalContentHeight = Math.max(totalContentHeight, y + 100 * this.scaleFactor); // Track content height

            // Term text
            const termText = this.add.text(
                -contentWidth / 2 + 20 * this.scaleFactor, y,
                this.glossary[index].subject,
                {
                    fontFamily: 'Space Grotesk',
                    fontSize: this.fontSize * 0.9,
                    fontWeight: '700',
                    color: '#11044E',
                    align: 'left',
                    resolution: 2,
                    fontStyle: 'bold'
                }
            ).setOrigin(0, 0);

            // Definition text
            const defText = this.add.text(
                -contentWidth / 2 + 20 * this.scaleFactor, y + 25 * this.scaleFactor,
                this.glossary[index].body,
                {
                    fontFamily: 'Space Grotesk',
                    fontSize: this.fontSize * 0.7,
                    fontWeight: '400',
                    color: '#11044E',
                    align: 'left',
                    resolution: 2,
                    wordWrap: { width: contentWidth - 40 * this.scaleFactor }
                }
            ).setOrigin(0, 0);

            // Divider line
            const divider = this.add.line(
                defText.x + 480 * this.scaleFactor, defText.y + 55 * this.scaleFactor,
                -contentWidth / 2 + 10 * this.scaleFactor, 0,
                contentWidth / 2 - 10 * this.scaleFactor, 0,
                0xBECCFF, 0.7
            );

            glossaryContainer.add([termText, defText, divider]);
        });

        // Define the viewable area (mask)
        const maskHeight = this.helpDeskBG.displayHeight - 100 * this.scaleFactor; // Leave some space for title
        const maskWidth = this.helpDeskBG.displayWidth + 200 * this.scaleFactor;

        // Position the mask to align with contentBG
        const maskShape = this.add.rectangle(
            0, // Center x position
            120 * this.scaleFactor, // Adjusted to leave space for title at top
            maskWidth,
            maskHeight,
            0xff53ff
        ).setVisible(false);

        const maskGraphics = this.make.graphics();
        maskGraphics.fillRectShape(maskShape);

        const geometryMask = new Phaser.Display.Masks.GeometryMask(this, maskGraphics);
        glossaryContainer.setMask(geometryMask);

        // Function to update glossary container position with bounds checking
        const updateGlossaryPosition = (newY) => {
            // Add padding to the bottom so last items can be seen clearly
            const bottomPadding = 50 * this.scaleFactor; // Adjust this value as needed

            // Ensure we don't scroll past content boundaries
            const boundedY = Phaser.Math.Clamp(
                newY,
                -totalContentHeight + maskHeight / 2 - bottomPadding, // Bottom boundary with padding
                0 // Top boundary
            );

            glossaryContainer.y = boundedY;
            updateScrollIndicator();
        };

        // Set up scrolling with mouse wheel
        this.input.on('wheel', (pointer, gameObjects, deltaX, deltaY, deltaZ) => {
            // Only respond to scroll when glossary is visible
            if (!this.isGlossaryVisible) return;

            const scrollSpeed = 1 * this.scaleFactor;
            updateGlossaryPosition(glossaryContainer.y - deltaY * scrollSpeed);
        });

        // Add dragging functionality (fixed to work correctly)
        let isDragging = false;
        let startY = 0;

        this.input.on('pointerdown', (pointer) => {
            if (!this.isGlossaryVisible) return;

            // playUISound(this, this.soundManager, "Subtle click 1", 0.25)
            // Check if pointer is within the contentBG area
            const contentBounds = this.contentBG.getBounds();
            if (contentBounds.contains(pointer.x, pointer.y)) {
                isDragging = true;
                startY = pointer.y - glossaryContainer.y;
            }
        });

        this.input.on('pointermove', (pointer) => {
            if (!isDragging) return;

            updateGlossaryPosition(pointer.y - startY);
        });

        this.input.on('pointerup', () => {
            isDragging = false;
        });

        // Add a scroll indicator
        const scrollIndicator = this.add.rectangle(
            contentWidth / 2 - 15 * this.scaleFactor,
            0,
            5 * this.scaleFactor,
            40 * this.scaleFactor,
            0xBECCFF
        ).setAlpha(0.7);

        // Function to update scroll indicator position
        const updateScrollIndicator = () => {
            if (!this.isGlossaryVisible) return;

            const scrollPercentage = -glossaryContainer.y / (totalContentHeight - maskHeight);
            const scrollbarTrackHeight = maskHeight - scrollIndicator.height;
            const newY = -maskHeight / 2 + scrollIndicator.height / 2 + (scrollPercentage * scrollbarTrackHeight);

            scrollIndicator.y = newY;
            scrollIndicator.setAlpha(0.7);

            // Hide indicator after a delay
            this.time.delayedCall(1500, () => {
                if (this.isGlossaryVisible) {
                    scrollIndicator.setAlpha(0.3);
                }
            });
        };

        // Add elements to content container
        this.contentContainer.add([glossaryContainer, scrollIndicator]);

        // Store references for later use
        this.glossaryContainer = glossaryContainer;
        this.glossaryMaskHeight = maskHeight;
        this.glossaryContentHeight = totalContentHeight;

        // Set initial state indicator
        this.isGlossaryVisible = true;

        // Initial update of scroll indicator
        updateScrollIndicator();
    }
}